import { useShowSavedForLater } from 'components/create-dialog/CreateDialogContext';
import { draftPageOf } from 'components/create-dialog/recordTypes';
import { draftTypeName, shareNames, sharerOf } from 'components/create-dialog/SavedForLaterList';
import { DraftDeletionChip } from 'components/drafts/DraftChip';
import EmptyState from 'components/EmptyState';
import { ClockIcon } from 'components/icons';
import { FormattedDate } from 'components/misc/FormattedDate';
import { Button } from 'components/ui/button';
import { isToday } from 'date-fns';
import useAuth from 'hooks/useAuth';
import { Link } from 'react-router-dom';
import { useGetDraftsQuery } from 'store/api/drafts';
import { DraftDetail } from 'types/drafts';
import { cn } from 'utils/cn';
import { formatTimeOfDay } from 'utils/date';
import { t } from 'utils/getTranslations';
import { useLastNameFirst } from 'utils/nameOrder';
import HomeSection, { HOME_LINK_CLASS } from './HomeSection';

/** A row's one line under the name: type, last save, what is left to fill, and who it is shared with or by. */
function Meta({ entry, userId }: { entry: DraftDetail; userId: number | undefined }) {
    const savedAt = new Date(entry.lastSavedAt);
    const lastNameFirst = useLastNameFirst();
    const sharer = sharerOf(entry, userId, lastNameFirst);
    const share = sharer
        ? t('create-dialog.saved.from', { name: sharer })
        : entry.shares.length > 0
          ? t('create-dialog.saved.with', { names: shareNames(entry, lastNameFirst) })
          : null;
    return (
        <span className="text-muted-foreground text-caption">
            {draftTypeName(entry.type)} ·{' '}
            {isToday(savedAt) ? (
                t('create-dialog.saved.today-at', { time: formatTimeOfDay(savedAt) })
            ) : (
                <FormattedDate value={savedAt} />
            )}{' '}
            ·{' '}
            {entry.missingFields.length > 0
                ? t('create-dialog.saved.fields-to-fill', { count: String(entry.missingFields.length) })
                : t('create-dialog.saved.ready')}
            {share ? ` · ${share}` : null}
        </span>
    );
}

/** Home's Saved for later: the drafts the person sees, latest save first, each a link to its page. */
export default function SavedForLaterSection() {
    const { user } = useAuth();
    const showAll = useShowSavedForLater();
    const { data: drafts, isError, refetch } = useGetDraftsQuery();
    const count = drafts?.length ?? 0;

    return (
        <HomeSection
            className="gap-1.5 pb-2.5"
            title={
                <>
                    {t('create-dialog.saved.title')}
                    {count > 0 ? (
                        <span className="bg-neutral-tint text-caption ms-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 align-middle font-semibold tabular-nums">
                            {count}
                        </span>
                    ) : null}
                </>
            }
            actions={
                count > 0 && showAll ? (
                    <Button variant="link" className={cn(HOME_LINK_CLASS, 'h-auto p-0')} onClick={showAll}>
                        {t('home.show-all')}
                    </Button>
                ) : null
            }
        >
            {!drafts ? (
                isError ? (
                    <EmptyState size="panel" variant="error" onRetry={refetch} className="min-h-0 flex-1 py-4" />
                ) : null
            ) : count === 0 ? (
                <EmptyState
                    size="panel"
                    icon={ClockIcon}
                    titleId="create-dialog.saved.empty"
                    className="min-h-0 flex-1 py-4"
                />
            ) : (
                <ul className="min-h-0 flex-1 overflow-y-auto">
                    {drafts.map((entry) => (
                        <li key={`${entry.type}/${entry.id}`} className="border-divider-soft border-t">
                            <Link
                                to={draftPageOf(entry.type, entry.id)}
                                className="text-foreground flex flex-col items-start gap-0.5 py-[9px] no-underline"
                            >
                                <span className="font-semibold">{entry.name ?? draftTypeName(entry.type)}</span>
                                <Meta entry={entry} userId={user?.id} />
                                <DraftDeletionChip deletesOn={entry.deletesOn} />
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </HomeSection>
    );
}
